import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Loader2, Printer, Trash2, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

/**
 * #1960 溜まっていた厨房伝票を、いきなり刷る前に一度聞く。
 *
 * プリンターを繋いだ（橋渡しを起動した）とたんに前の命令が一斉に印刷され、
 * **何枚も出てくるのに止める方法が無かった**。「自動印刷を止める」トグルは
 * これから先を止めるだけで、溜まったぶんには効かない。
 *
 * 1〜2 件は通常営業の遅れなので黙って刷る（毎回聞くと厨房の邪魔になる）。
 * このダイアログが出るのは **3 件以上**溜まったとき＝「しばらく繋がって
 * いなかった」ときだけ。
 *
 * ## 「破棄」で紙に出さないだけで、注文は消えない
 *
 * サーバの `kitchenPrintedAt` を立てるので端末を跨いでも復活しないが、
 * 厨房ページの「最近の伝票」には残る。**注文そのものを消す操作ではない**ことを
 * 文面でも伝える。
 */
export default function PendingPrintDialog({
  count, onPrintAll, onDiscard, onSnooze,
}: {
  count: number
  onPrintAll: () => Promise<void>
  onDiscard: () => Promise<void>
  onSnooze: () => void
}) {
  const { t } = useI18n()
  const [busy, setBusy] = useState<'print' | 'discard' | null>(null)

  const run = async (kind: 'print' | 'discard', fn: () => Promise<void>) => {
    if (busy) return
    setBusy(kind)
    try {
      await fn()
    } finally {
      setBusy(null)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-5">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-gold" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-bold text-text-primary">
              {t('storeKitchen.pendingBurstTitle', { count: String(count) })}
            </p>
            <p className="mt-1.5 font-body text-xs leading-relaxed text-text-secondary">
              {t('storeKitchen.pendingBurstDesc', { count: String(count) })}
            </p>
          </div>
          <button
            onClick={onSnooze}
            disabled={busy !== null}
            className="shrink-0 rounded-full p-1 tap-effect hover:bg-gray-100 disabled:opacity-50"
            aria-label={t('storeKitchen.pendingBurstLater')}
          >
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <button
            onClick={() => void run('print', onPrintAll)}
            disabled={busy !== null}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-navy font-display text-sm font-semibold text-white tap-effect disabled:opacity-50"
          >
            {busy === 'print' ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            {t('storeKitchen.pendingBurstPrintAll', { count: String(count) })}
          </button>
          <button
            onClick={() => void run('discard', onDiscard)}
            disabled={busy !== null}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-yokoso-red/40 bg-white font-display text-sm font-semibold text-yokoso-red tap-effect disabled:opacity-50"
          >
            {busy === 'discard' ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {t('storeKitchen.pendingBurstDiscard')}
          </button>
        </div>

        <p className="mt-3 font-body text-[11px] leading-relaxed text-text-tertiary">
          {t('storeKitchen.pendingBurstDiscardHint')}
        </p>
      </div>
    </div>,
    document.body,
  )
}
